/**
 * Turning a search MATCH into the excerpt of text it actually promises to be about.
 *
 * Split out of `helpContext.ts` as its own dependency-free module (VRG-227) so it can be
 * exercised directly by a node-run guard spec — `helpContext.ts` itself pulls in `DOCS` and
 * `searchDocEntries` through the Vite path aliases `docs` / `search/scoring`, which resolve
 * fine under the bundler but not under bare node (see `test/scoring.guard.spec.ts`'s own
 * note on why aliased VALUE imports are avoided in anything a guard imports directly). This
 * module only ever touches `DocEntry` / `DocHeading` as TYPES (erased by node's type
 * stripping) plus plain strings, so it has no such dependency.
 *
 * See `helpContext.ts` for why a heading-level match needs this at all: `search-index.json`
 * carries one excerpt per DOCUMENT (its lead paragraph), not one per heading, so every
 * heading-level result used to be handed the same intro text regardless of which section it
 * actually matched.
 */
import type { DocEntry, DocHeading } from 'docs'

/**
 * Ceiling on a section's excerpt, in characters.
 *
 * A whole section (especially the last one in a guide, which runs to end-of-document) can
 * run past 2,000 characters — the certificates guide's closing "Walkthrough" section is
 * over 2,100. Five of those would be most of the prompt before the model has said a word.
 * 1,000 sits inside the "enough to actually answer, short enough that five still fit" range:
 * long enough to carry a full numbered step (the transfer walkthrough step is ~250
 * characters) plus its neighbours for context, short enough that MAX_CONTEXT (5) entries
 * stay a few thousand characters total rather than tens of thousands.
 */
export const MAX_EXCERPT_CHARS = 1000

/**
 * The prose that belongs to one heading: everything from the line after it up to (but
 * excluding) the next heading in document order — regardless of that next heading's depth,
 * because a heading's section ends the instant ANY new heading begins, not just a sibling at
 * the same depth. `line` is 1-based (see `DocHeading`), so the 0-based array index of the
 * line immediately after the heading is numerically `heading.line` itself: 1-based line N
 * sits at 0-based index N-1, so line N+1 sits at index N.
 */
export const sliceSection = (body: string, heading: DocHeading, headings: DocHeading[]): string => {
  const lines = body.split('\n')
  const index = headings.findIndex(h => h.id === heading.id && h.line === heading.line)
  const next = index === -1 ? undefined : headings[index + 1]
  const end = next ? next.line - 1 : lines.length
  return lines.slice(heading.line, end).join('\n')
}

/**
 * Strip the markdown syntax that costs tokens without helping the model, while keeping the
 * things that carry meaning: list structure (`-`, numbering) and prose stay untouched.
 *
 *  - Heading hashes: defensive only — `sliceSection` already excludes a heading's own line,
 *    but a stray `#`-prefixed line should never survive if the boundary math is ever wrong.
 *  - Link syntax `[text](url)`: the URL is dead weight for an answer grounded in prose; the
 *    link text alone reads as a normal sentence.
 *  - HTML comments: guides carry `<!-- vera:internal -->` blocks (§43) that the publisher is
 *    supposed to have already stripped from the customer artifact, but stripping any comment
 *    that slipped through here is a second, cheap line of defense against leaking internal
 *    notes into a customer-facing prompt.
 */
export const stripMarkdownNoise = (text: string): string =>
  text
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')

/**
 * Cap `text` to at most `max` characters without cutting mid-word.
 *
 * Backs off to the last whitespace boundary before `max`, unless that boundary is so far
 * back it would throw away most of the budget (a single very long unbroken token) — in
 * which case a hard cut beats returning a near-empty excerpt.
 */
export const capExcerpt = (text: string, max: number): string => {
  const trimmed = text.trim()
  if (trimmed.length <= max) return trimmed
  const cut = trimmed.slice(0, max)
  const lastSpace = cut.lastIndexOf(' ')
  const safe = lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut
  return `${safe.trimEnd()}…`
}

/** One search result, ranged over a doc and — for a section-level result — its heading. */
export interface SectionMatch {
  doc: DocEntry
  heading?: DocHeading
}

/**
 * The excerpt for one match: a heading's own section, sliced/cleaned/capped, when there is
 * one; the doc's own lead paragraph otherwise (a page-level match — the query hit the
 * title/tags/excerpt fields, not a specific heading — is correctly represented by "here is
 * the whole guide's intro").
 *
 * `getBody` is injected rather than read from a module-level map so this function stays
 * synchronous and side-effect-free: `helpContext.ts` supplies it after awaiting the lazy
 * bodies chunk; a guard spec supplies it straight from the already-loaded generated map.
 * Falls back to the lead excerpt if the body is missing, or if the sliced section turns out
 * empty (e.g. a heading immediately followed by another with no prose in between) — either
 * way, never grounds the model in nothing.
 */
export const excerptForMatch = (match: SectionMatch, getBody: (doc: DocEntry) => string): string => {
  if (!match.heading) return match.doc.excerpt

  const body = getBody(match.doc)
  if (!body) return match.doc.excerpt

  const section = stripMarkdownNoise(sliceSection(body, match.heading, match.doc.headings)).trim()
  return section ? capExcerpt(section, MAX_EXCERPT_CHARS) : match.doc.excerpt
}
